import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, LineChart as ChartIcon, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProgressChart } from "./ProgressChart";

const AREAS = [["attitude", "Attitude"], ["behavior", "Behavior"], ["skill", "Skill"]];
const avg = (xs) => (xs.length ? (xs.reduce((a, b) => a + b, 0) / xs.length).toFixed(1) : "—");

function ScoreRow({ label, value, onPick, testid }) {
  return (
    <div className="flex items-center gap-2" role="radiogroup" aria-label={label}>
      <span className="w-20 text-sm font-semibold text-[#1a1617]">{label}</span>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} role="radio" aria-checked={value === n} onClick={() => onPick(value === n ? null : n)}
          data-testid={`${testid}-${n}`}
          className={`w-9 h-9 rounded-full text-sm font-bold border transition-colors duration-150 ${value === n ? "bg-[#920e12] text-white border-[#920e12]" : "bg-white text-[#1a1617] border-[#d6cecf] hover:border-[#920e12]"}`}>
          {n}
        </button>
      ))}
    </div>
  );
}

function ParticipantEval({ p, row, history, onSave }) {
  const [note, setNote] = useState(row.note || "");
  const [showChart, setShowChart] = useState(false);
  useEffect(() => { setNote(row.note || ""); }, [row.note]);
  const pid = p.participant_id;
  return (
    <div className="bg-white rounded-2xl border border-[#dfd8da] p-4 sm:p-5" data-testid={`eval-row-${pid}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-lg font-bold text-[#1a1617]">{p.name}</h3>
        <span className="text-xs text-[#545454]" data-testid={`eval-avg-${pid}`}>
          Season avg · {AREAS.map(([k, l]) => `${l[0]} ${avg(history.map((h) => h[k]).filter(Boolean))}`).join(" · ")}
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {AREAS.map(([k, l]) => (
          <ScoreRow key={k} label={l} value={row[k] ?? null} testid={`eval-${k}-${pid}`} onPick={(v) => onSave(p, { [k]: v })} />
        ))}
      </div>
      <label className="block mt-3 text-sm font-semibold text-[#1a1617]" htmlFor={`note-${pid}`}>Weekly review (optional)</label>
      <textarea id={`note-${pid}`} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
        onBlur={() => { if (note !== (row.note || "")) onSave(p, { note }); }}
        data-testid={`eval-note-${pid}`} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#d6cecf] text-sm"
        placeholder="What did they do well? What to work on next week?" />
      <button type="button" onClick={() => setShowChart((v) => !v)} aria-expanded={showChart} data-testid={`eval-chart-toggle-${pid}`}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#920e12] hover:underline">
        <ChartIcon size={15} aria-hidden="true" /> {showChart ? "Hide progress" : "Show progress"}
      </button>
      {showChart && <div data-testid={`eval-chart-${pid}`}><ProgressChart history={history} name={p.name} /></div>}
    </div>
  );
}

export function EvaluationsPanel({ classId, roster }) {
  const [rows, setRows] = useState([]);
  const [week, setWeek] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/classes/${classId}/evaluations.xlsx`, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "IM Weekly Evaluations.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not export the spreadsheet"); }
    finally { setExporting(false); }
  };

  useEffect(() => {
    api.get(`/classes/${classId}/evaluations`).then(({ data }) => {
      setRows(data);
      if (data.length) setWeek(Math.max(...data.map((r) => r.week)));
      setLoaded(true);
    }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load evaluations"));
  }, [classId]);

  const byKey = useMemo(() => Object.fromEntries(rows.map((r) => [`${r.participant_id}:${r.week}`, r])), [rows]);

  const save = async (p, patch) => {
    const match = (r) => r.participant_id === p.participant_id && r.week === week;
    setRows((rs) => rs.some(match) ? rs.map((r) => (match(r) ? { ...r, ...patch } : r)) : [...rs, { participant_id: p.participant_id, week, ...patch }]);
    try {
      await api.put("/evaluations", { class_id: classId, participant_id: p.participant_id, week, ...patch });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
  };

  const done = roster.filter((p) => { const r = byKey[`${p.participant_id}:${week}`]; return r && r.attitude && r.behavior && r.skill; }).length;

  return (
    <div data-testid="evaluations-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeek((w) => Math.max(1, w - 1))} aria-label="Previous week" data-testid="eval-week-prev" className="p-2 rounded-full border border-[#dfd8da] hover:bg-[#920e12] hover:text-white transition-colors duration-150"><ChevronLeft size={18} /></button>
          <span className="text-xl font-bold text-[#550001] w-24 text-center" data-testid="eval-week-label">Week {week}</span>
          <button onClick={() => setWeek((w) => Math.min(20, w + 1))} aria-label="Next week" data-testid="eval-week-next" className="p-2 rounded-full border border-[#dfd8da] hover:bg-[#920e12] hover:text-white transition-colors duration-150"><ChevronRight size={18} /></button>
        </div>
        <p className="text-sm text-[#545454]" data-testid="eval-progress">{done} of {roster.length} fully scored · 1 = needs support, 5 = excellent · saves automatically{week > 10 && " · the spreadsheet export covers weeks 1–10"}</p>
        <Button size="sm" variant="outline" onClick={exportXlsx} disabled={exporting || roster.length === 0} data-testid="eval-export-btn" className="border-[#920e12] text-[#920e12]">
          {exporting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} Export spreadsheet
        </Button>
      </div>
      <fieldset disabled={!loaded} className="mt-5 space-y-3">
        {roster.map((p) => (
          <ParticipantEval key={`${p.participant_id}-${week}`} p={p} row={byKey[`${p.participant_id}:${week}`] || {}}
            history={rows.filter((r) => r.participant_id === p.participant_id)} onSave={save} />
        ))}
        {roster.length === 0 && <p className="text-[#545454]">No participants enrolled in this class yet.</p>}
      </fieldset>
    </div>
  );
}
